import { motion, AnimatePresence } from "framer-motion";
import { X, Copy, Check, Send } from "lucide-react";
import { useState } from "react";

const PLATFORMS = [
  { id: "hifireels", name: "Hifireels", color: "#00E5FF", icon: "📱", internal: true },
  { id: "whatsapp", name: "WhatsApp", color: "#25D366", icon: "💬", url: (text, link) => `https://wa.me/?text=${encodeURIComponent(text + " " + link)}` },
  { id: "instagram", name: "Instagram", color: "#E4405F", icon: "📷", url: (text, link) => null, copy: true },
  { id: "facebook", name: "Facebook", color: "#1877F2", icon: "👍", url: (text, link) => `https://www.facebook.com/sharer.php?u=${encodeURIComponent(link)}` },
  { id: "twitter", name: "Twitter/X", color: "#1DA1F2", icon: "🐦", url: (text, link) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(link)}` },
  { id: "telegram", name: "Telegram", color: "#0088cc", icon: "✈️", url: (text, link) => `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}` },
  { id: "linkedin", name: "LinkedIn", color: "#0A66C2", icon: "💼", url: (text, link) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}` },
  { id: "snapchat", name: "Snapchat", color: "#FFFC00", icon: "👻", url: (text, link) => `https://www.snapchat.com/scan?attachmentUrl=${encodeURIComponent(link)}` },
  { id: "reddit", name: "Reddit", color: "#FF4500", icon: "🤖", url: (text, link) => `https://reddit.com/submit?url=${encodeURIComponent(link)}&title=${encodeURIComponent(text)}` },
  { id: "email", name: "Email", color: "#EA4335", icon: "✉️", url: (text, link) => `mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent(text + "\n\n" + link)}` },
  { id: "copy", name: "Copy Link", color: "#94A3B8", icon: "🔗", copy: true },
];

export default function ShareSheet({ url, title, onClose, onSendInApp }) {
  const [copied, setCopied] = useState(false);
  const [msg, setMsg] = useState("");

  const handleShare = async (p) => {
    if (p.id === "hifireels") {
      onSendInApp?.();
      return;
    }
    if (p.id === "copy") {
      try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(()=>setCopied(false), 1500); }
      catch { setMsg("Copy failed"); }
    } else if (p.id === "instagram") {
      try {
        await navigator.clipboard.writeText(url);
        setMsg("Link copied! Paste in Instagram DM/Story/Bio");
        setTimeout(()=>setMsg(""), 2500);
      } catch {}
    } else {
      const link = p.url(title, url);
      if (link) window.open(link, "_blank", "noopener,noreferrer,width=600,height=600");
    }
  };

  return (
    <AnimatePresence>
      <motion.div className="fixed inset-0 z-50 max-w-md mx-auto" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
        <div className="absolute inset-0 bg-black/70" onClick={onClose} />
        <motion.div initial={{y:"100%"}} animate={{y:0}} exit={{y:"100%"}} transition={{type:"spring", damping:30}}
          className="absolute bottom-0 inset-x-0 glass rounded-t-3xl p-5" data-testid="share-sheet">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-outfit font-bold text-lg">Share</h3>
            <button onClick={onClose} data-testid="share-close"><X className="w-6 h-6"/></button>
          </div>

          <div className="mb-4 p-3 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-between">
            <div className="text-xs text-white/70 truncate flex-1 mr-2">{url}</div>
            <button onClick={()=>handleShare(PLATFORMS[PLATFORMS.length - 1])} data-testid="share-copy-quick" className="px-3 py-1.5 rounded-full bg-[#00E5FF] text-black text-xs font-bold flex items-center gap-1">
              {copied ? <><Check className="w-3 h-3"/>Copied</> : <><Copy className="w-3 h-3"/>Copy</>}
            </button>
          </div>

          {msg && <div className="mb-3 text-xs text-[#00E5FF] text-center">{msg}</div>}

          <div className="grid grid-cols-4 gap-3">
            {PLATFORMS.map(p => (
              <button key={p.id} onClick={()=>handleShare(p)} data-testid={`share-${p.id}`}
                className={`flex flex-col items-center gap-1.5 p-3 rounded-2xl bg-white/5 border border-white/10 active:scale-95 transition ${p.internal ? "ring-2 ring-[#00E5FF]/40" : ""}`}>
                <div className="w-11 h-11 rounded-full flex items-center justify-center text-2xl relative" style={{background: p.color + "22", borderColor: p.color, borderWidth: 1}}>
                  {p.internal ? <Send className="w-5 h-5 text-[#00E5FF]"/> : p.icon}
                </div>
                <span className={`text-[10px] ${p.internal ? "text-[#00E5FF] font-bold" : "text-white/80"}`}>{p.name}</span>
              </button>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
